// @ts-check
// The TypeScript engine will check all JavaScript in this file.

import PubSubUtils from '../utils/pubsub-utils';
import { publicConstants } from '../constants';

import { trimURL } from './utils';

/**
 * @file Implements Browser History Management Logic: consists of methods for handling change in browser history
 * @author WebWiz Team <WebWizDevelopmentTeam@pega.com>
 */

/**
 * this method update browser document title
 * @function updateDocumentTitle
 * @param title - browser document title
 */
export const updateDocumentTitle = (title: string) => {
  window.document.title = title;
};

interface SemanticURLInfo {
  [key: string]: unknown;
  options?: Record<string, unknown>;
}

interface HistoryState {
  stateObj: SemanticURLInfo;
}

/**
 * this method is used to formulate the state for corresponding semantic url
 * @function stateForSemanticURL
 * @param info - information corresponding to semantic url
 * @param options - other params like context
 * @returns state corresponding to semantic url
 */
export const getStateForSemanticURL = (info: SemanticURLInfo, options: Record<string, unknown>) => {
  // FIXME:
  if (info !== undefined) {
    info.options = options;
  }

  return { stateObj: info };
};

/**
 * this method is a wrapper function for history.pushState and implements the same in try-catch block
 * @function addToHistory
 * @param state - state information corresponding to semantic url
 * @param title - title corresponding to semantic url
 * @param url - semantic url
 */
export const addToHistory = (state: HistoryState, title: string, url: string) => {
  try {
    window.history.pushState(state, title, url);
    window.scrollTo(0, 0);
  } catch (err) {
    throw new Error('Error- Unable to Add to Browser History');
  }
};

/**
 * this method is a wrapper function for history.replaceState and implements the same in try-catch block
 * @function replaceHistory
 * @param state - state information corresponding to semantic url
 * @param title - title corresponding to semantic url
 * @param url - semantic url
 */
export const replaceHistory = (state: HistoryState, title: string, url: string) => {
  try {
    window.history.replaceState(state, title, url);
  } catch (err) {
    throw new Error('Error- Unable to Replace Browser History');
  }
};

/**
 * this method returns history state on Browser back/forward action
 * @function getStateForBackForwardNav
 * @param event - popstate event
 * @returns state corresponding to popstate event
 */
export const getStateForBackForwardNav = (event: PopStateEvent) => {
  if (event.state?.type === 'ui-navigation') {
    PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.URL_NAVIGATION, event.state);
    return null;
  }
  return event.state ? event.state.stateObj : null;
};

/**
 * this method add a Event listener for browser back/forward event
 * @function addEventListenerForHistory
 * @param handlerFunc - event handler function reference
 */
export const addEventListenerForHistory = (handlerFunc: Function) => {
  // Adding Event Listener for Browser Back/Forward event
  window.addEventListener('popstate', (event) => handlerFunc(event));
};

/**
 * this method add a Event listener for browser refresh event
 * @function addEventListenerForRefresh
 */
export const addEventListenerForRefresh = () => {
  // Adding onbeforeunload event to allow user to intercept an attempt to refresh the page.
  window.onbeforeunload = () => null;
};

/**
 * this method returns the pathname of a URL
 * @function getPathNameOfUrl
 * @returns pathname of URL with first and last '/' removed
 */
export const getPathNameOfUrl = () => {
  return trimURL(window.location.pathname);
};

/**
 * this method returns the pathname of a URL
 * @function getPathNameOfUrl
 * @returns pathname of URL with first and last '/' removed
 */
export const getPathNameWithQueryParams = () => {
  return trimURL(window.location.pathname + window.location.search);
};

/**
 * this method returns URL
 * @function getFullUrl
 * @returns complete URL
 */
export const getFullUrl = () => {
  return window.location.href;
};

/**
 * This method returns url search parameters
 * @function getQueryParams
 * @returns complete URL
 */
export const getQueryParams = () => {
  return window.location.search;
};

/**
 * this method displays a message in Browser console
 * @function sendMessageToConsole
 * @param message - message
 * @param additionalInfo - additional Information to be displayed with message
 * @param messageType - warning | error | info
 */
export const sendMessageToConsole = (message: string | Error, additionalInfo: unknown, messageType: string) => {
  switch (messageType) {
    case 'warning':
      if (additionalInfo) {
        console.warn(message, additionalInfo);
      } else {
        console.warn(message);
      }
      break;

    case 'error':
      if (additionalInfo) {
        console.error(message, additionalInfo);
      } else {
        console.error(message);
      }
      break;

    default:
      if (additionalInfo) {
        console.log(message, additionalInfo);
      } else {
        console.error(message);
      }
  }
};
